/**
 * THE ONE PLACE THE KYC & AML STEP IS PRICED FOR DISPLAY.
 *
 * Canvas, node card, configuration panel and Publish used to price this step
 * four different ways, and for the same unchanged flow they disagreed:
 *
 *   node card rows   metadata price, else FELL BACK to the `allId` action price
 *   node card total  metadata price for three hardcoded keys, NO fallback
 *   config panel     every priced row whose setting was truthy, AML left out
 *   Publish          the flow's saved snapshot first, then the catalog
 *
 * The fallback was the worst of it. `/pipeline-init` overwrites the `allId`
 * action price with the Didit data-validation FLOOR (the cheapest country), so a
 * row whose catalog key was missing quoted Global Document Validation and
 * Criminal Background Check at the data-validation floor — a price that belongs
 * to neither — while the total beside them silently counted them as nothing.
 *
 * WHAT IS QUOTED IS WHAT IS BILLED. Every figure here comes from `billedPrice`
 * when the catalog carries one, which is the business price book's
 * `price.domestic` — the field `SubscriptionDetails.updateItemQuantity` and
 * `pipeline-pricing` charge from. `/pipeline-init` keeps the vendor figure in
 * `price` for the per-country rate drawer, and stashes the billed one alongside
 * it (see `applyVendorPricing`). Rows the vendor overlay never touches carry no
 * `billedPrice`, because their `price` already IS the price book.
 *
 * Everything here is pure so it can be tested without a browser.
 */
import { isRowOn } from '../components/kyc-action/row-default';

/** A catalog metadata row, or an action — both carry the same price fields. */
export interface IPriced {
	key?: string;
	price?: unknown;
	billedPrice?: unknown;
	default?: unknown;
}

/** What the customer is billed for one row or action. Never the vendor figure. */
export const billedPrice = (item: IPriced | undefined): number => {
	const n = Number(item?.billedPrice ?? item?.price ?? 0);
	return Number.isFinite(n) ? n : 0;
};

/**
 * The catalog key each priced check lives under, newest first.
 *
 * THE OLDER NAMES ARE HERE ON PURPOSE. A deployment's `master-pipelines` is
 * seeded from `steps-actions.json` only while the collection is EMPTY
 * (`InitService.create`), so an environment seeded before the rename keeps the
 * old keys for good — redeploying does not refresh them. The node card looked
 * up only the new names, missed on such a catalog, and fell back to the wrong
 * price. Trying the old name second prices the same check correctly on both.
 */
export const KYC_CHECK_KEYS: { [check: string]: string[] } = {
	documentValidation: ['documentValidation', 'idv'],
	dataValidation: ['dataValidation', 'addressVerification'],
	ssnVerification: ['ssnVerification', 'ssn'],
	criminalBackgroundCheck: ['criminalBackgroundCheck'],
};

/** The tile's row ids, mapped to the check each one prices. */
export const KYC_ROW_CHECK: { [labelId: string]: string } = {
	verify: 'documentValidation',
	address: 'dataValidation',
	ssnVerification: 'ssnVerification',
	criminalBackgroundCheck: 'criminalBackgroundCheck',
};

/**
 * The checks that add to the step's price. SSN is deliberately absent: it rides
 * on the data-validation call, so the product quotes it free — and checkout does
 * not charge it either (see `pipeline-pricing`).
 */
export const KYC_PRICED_CHECKS = [
	'documentValidation',
	'dataValidation',
	'criminalBackgroundCheck',
] as const;

/** The catalog row for a check, under whichever name this catalog uses. */
export const findKycRow = (
	metadata: IPriced[] | undefined,
	check: string
): IPriced | undefined => {
	const names = KYC_CHECK_KEYS[check] ?? [check];
	for (const name of names) {
		const hit = (metadata ?? []).find(row => row?.key === name);
		if (hit) return hit;
	}
	return undefined;
};

/**
 * The billed price of one tile row, or `undefined` when the catalog does not
 * price it. `undefined` is deliberate: it means "show no price", which is honest,
 * where falling back to some other row's price is not.
 */
export const kycRowPrice = (
	metadata: IPriced[] | undefined,
	labelId: string
): number | undefined => {
	const check = KYC_ROW_CHECK[labelId] ?? labelId;
	if (check === 'ssnVerification') return 0;
	const row = findKycRow(metadata, check);
	return row ? billedPrice(row) : undefined;
};

interface IKycTotal {
	/** The KYC step's `actions[0].metadata` from the live catalog. */
	metadata: IPriced[] | undefined;
	/** This node's settings (`ComplexSettingFormState[nodeId]`). */
	settings: Record<string, unknown> | undefined;
	/** The `amlVerification` action from the catalog's `aml` step. */
	amlAction?: IPriced;
}

/**
 * One node's total — the figure the canvas, the node card and the configuration
 * panel must all show. A row counts when it is ticked (`isRowOn`, the tile's own
 * rule, so an unset $5 opt-in is never billed by omission) and the catalog prices
 * it; PEPs & Sanctions counts when explicitly on.
 */
export const kycNodeTotal = ({ metadata, settings, amlAction }: IKycTotal): number => {
	const checks = KYC_PRICED_CHECKS.reduce((sum, check) => {
		const row = findKycRow(metadata, check);
		if (!row) return sum;
		return isRowOn(settings?.[check], row) ? sum + billedPrice(row) : sum;
	}, 0);
	const aml = settings?.amlVerification === true ? billedPrice(amlAction) : 0;
	return checks + aml;
};

/** Any KYC settings key, current or older, -> the check it belongs to. */
const KEY_TO_CHECK: { [key: string]: string } = Object.entries(KYC_CHECK_KEYS).reduce(
	(acc, [check, names]) => {
		names.forEach(name => {
			acc[name] = check;
		});
		return acc;
	},
	{} as { [key: string]: string }
);

/** The check a settings key prices, or undefined when it is not a KYC check. */
export const kycCheckOf = (key: string): string | undefined => KEY_TO_CHECK[key];

/**
 * The billed price of a KYC settings key, for screens that walk the flow's
 * settings by KEY (Publish). The same figure the node card shows for that check.
 */
export const kycCheckPrice = (metadata: IPriced[] | undefined, key: string): number => {
	const check = kycCheckOf(key) ?? key;
	if (check === 'ssnVerification') return 0;
	return billedPrice(findKycRow(metadata, check));
};

/**
 * Collapse a list of settings keys so each KYC check appears ONCE.
 *
 * A node configured against an older catalog carries BOTH names for a check —
 * the tile writes `dataValidation`, the older catalog's default seeds
 * `addressVerification` — and a screen that prices by key would otherwise bill
 * the one check twice. The first occurrence wins; non-KYC keys pass through.
 */
export const dedupeKycKeys = (keys: string[]): string[] => {
	const seen = new Set<string>();
	return keys.filter(key => {
		const check = kycCheckOf(key);
		if (!check) return true;
		if (seen.has(check)) return false;
		seen.add(check);
		return true;
	});
};

/** The `amlVerification` action, wherever the catalog keeps it. */
export const findAmlAction = (steps: any[] | undefined): IPriced | undefined => {
	const amlStep = (steps ?? []).find((s: any) => s?.key === 'aml');
	return (amlStep?.actions ?? []).find((a: any) => a?.key === 'amlVerification');
};
